import { db } from '@/db';
import { getPngDimensions } from './imageFiles';
import {
  Dimensions,
  Crop,
} from '@t/LayoutTypes';


interface BlobImage {
  blob: Blob;
  url: string;
  full: Dimensions;
  crop: Crop;
}

interface BlobImageSeries {
  blobs: Blob[];
  urls: string[];
  full: Dimensions;
  crop: Crop;
}

interface BlobImageSet {
  BSCAN: BlobImageSeries;
  RED: BlobImage;
  FAF: BlobImage;
  FAFONH: BlobImage;
  filenames: string[];
}


class ImageCache
{
  private _imageCount: number;
  private _images: {
    OD: null | BlobImageSet,
    OS: null | BlobImageSet,
  };

  get imageCount() {
    return this._imageCount;
  }
  get images() {
    return this._images;
  }

  constructor() {
    this.init();
  }

  private init() {
    this._imageCount = 0;
    this._images = { OS: null, OD: null };
  }

  async load() {
    const ct = await db.metaWhereUser().count();
    if (ct < 1) {
      this.clear();
      return;
    }
    const [imagesOD, imagesOS] = await Promise.all([
      loadEye('OD'),
      loadEye('OS'),
    ]);
    this._imageCount = ct;
    this._images.OD = imagesOD;
    this._images.OS = imagesOS;
  }

  clear() {
    if (this._images.OD)
      revokeUrls(this._images.OD);
    if (this._images.OS)
      revokeUrls(this._images.OS);
    this.init();
  }
};


async function loadEye(eye: 'OD' | 'OS'): Promise<BlobImageSet> {
  const metas = await db.metaWhereUser().filter(row => row.eye == eye).sortBy('filepath');
  const metaFAF = metas.find(row => row.type == 'FAF');
  const metaFAFONH = metas.find(row => row.type == 'FAFONH');
  const metaOCTs = metas.filter(row => row.type == 'OCT');
  const metaProps = metas.filter(row => row.type.includes('Properties'));
  const octFetches = metaOCTs.map(row => db.getPng(row.filepath));
  const [pngFAF, pngFAFONH] = await Promise.all([
    db.getPng(metaFAF!.filepath),
    db.getPng(metaFAFONH!.filepath),
  ]);
  const pngOCTs = await Promise.all(octFetches);
  const dimOCT = getPngDimensions(pngOCTs[0]);
  const dimFAF = getPngDimensions(pngFAF);
  const octBlobs = pngOCTs.map(b => new Blob([b], { type: 'image/png' }));
  const fafBlob = new Blob([pngFAF], { type: 'image/png' });
  const fafonhBlob = new Blob([pngFAFONH], { type: 'image/png' });
  const octLastFile = metaOCTs[metaOCTs.length-1].filepath;
  const octUrls = octBlobs.map(b => URL.createObjectURL(b));
  return {
    BSCAN: {
      blobs: octBlobs,
      urls: octUrls,
      full: dimOCT,
      crop: { ...metaOCTs![0].sub_images['BSCAN'], width: dimOCT.width - metaOCTs![0].sub_images['RED'].width },
    },
    RED: {
      blob: octBlobs[0],
      url: octUrls[0],
      full: dimOCT,
      crop: { ...metaOCTs![0].sub_images['RED'] },
    },
    FAF: {
      blob: fafBlob,
      url: URL.createObjectURL(fafBlob),
      full: dimFAF,
      crop: { ...metaFAF!.sub_images['FAF'] },
    },
    FAFONH: {
      blob: fafonhBlob,
      url: URL.createObjectURL(fafonhBlob),
      full: dimFAF,
      crop: { ...metaFAFONH!.sub_images['FAF'] },
    },
    filenames: [
      metaFAF!.filepath,
      metaFAFONH!.filepath,
      octLastFile,
      ...metaProps.map(row => row.filepath),
    ],
  };
}

function revokeUrls(imageSet: BlobImageSet) {
  imageSet.BSCAN.urls.forEach(url => URL.revokeObjectURL(url));
  URL.revokeObjectURL(imageSet.FAF.url);
  URL.revokeObjectURL(imageSet.FAFONH.url);
}


let cache = new ImageCache();

export default cache;
export {
  ImageCache,
  type BlobImageSet,
};
